import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Box, CircularProgress, Typography } from '@mui/material';
import NoPhotographyOutlinedIcon from '@mui/icons-material/NoPhotographyOutlined';
import { QrReader } from 'react-qr-reader';
import { useEventsApi } from 'services/api';
import { CHECK_IN_COLORS } from './colors';
import { CheckInResultFlood } from './CheckInResultFlood';
import { ScanResultState, toResultKind } from './types';

interface ScanTabProps {
  eventId: string;
  method: 'self_scan' | 'staff_scan';
  showScanNext?: boolean;
  onClose?: () => void;
  onShowMyCode?: () => void;
  initialResult?: ScanResultState | null;
  onResult?: (result: ScanResultState) => void;
}

const openDeviceSettings = (): void => {
  const bridge = (
    window as Window & {
      ReactNativeWebView?: { postMessage: (message: string) => void };
    }
  ).ReactNativeWebView;
  if (bridge?.postMessage) {
    bridge.postMessage(JSON.stringify({ type: 'OPEN_APP_SETTINGS' }));
    return;
  }

  const ua = window.navigator.userAgent || '';
  if (/android/i.test(ua)) {
    window.location.href =
      'intent:#Intent;action=android.settings.APPLICATION_DETAILS_SETTINGS;end';
    return;
  }
  if (/iPhone|iPad|iPod/i.test(ua)) {
    window.location.href = 'app-settings:';
  }
};

export const ScanTab: React.FC<ScanTabProps> = ({
  eventId,
  method,
  showScanNext,
  onClose,
  onShowMyCode,
  initialResult = null,
  onResult,
}) => {
  const eventsApi = useEventsApi();
  const [result, setResult] = useState<ScanResultState | null>(initialResult);
  const [scanning, setScanning] = useState(!initialResult);
  const [busy, setBusy] = useState(false);
  const [camera, setCamera] = useState<'checking' | 'ready' | 'denied'>(
    initialResult ? 'ready' : 'checking',
  );
  const lastCodeRef = useRef<string | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const audience = method === 'staff_scan' ? 'organizer' : 'attendee';

  useEffect(() => {
    if (initialResult) {
      setResult(initialResult);
      setScanning(false);
    }
  }, [initialResult]);

  const probeCamera = useCallback(async () => {
    if (!navigator.mediaDevices?.getUserMedia) {
      setCamera('denied');
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'environment' },
      });
      stream.getTracks().forEach(track => track.stop());
      setCamera('ready');
    } catch {
      setCamera('denied');
    }
  }, []);

  useEffect(() => {
    if (initialResult) return;
    void probeCamera();
  }, [initialResult, probeCamera]);

  useEffect(() => {
    if (camera !== 'denied') return undefined;
    const onVisible = () => {
      if (document.visibilityState === 'visible') void probeCamera();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [camera, probeCamera]);

  const stopCamera = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
    const video = containerRef.current?.querySelector('video');
    const domStream = video?.srcObject as MediaStream | null;
    if (domStream) {
      domStream.getTracks().forEach(track => track.stop());
    }
    if (video) video.srcObject = null;
  }, []);

  useEffect(() => {
    if (!scanning || camera !== 'ready') return undefined;
    let raf = 0;
    const capture = () => {
      const video = containerRef.current?.querySelector('video');
      const stream = (video?.srcObject as MediaStream) || null;
      if (stream) {
        streamRef.current = stream;
      } else {
        raf = window.requestAnimationFrame(capture);
      }
    };
    raf = window.requestAnimationFrame(capture);
    return () => window.cancelAnimationFrame(raf);
  }, [camera, scanning]);

  useEffect(() => {
    if (!scanning) stopCamera();
  }, [scanning, stopCamera]);

  useEffect(() => () => stopCamera(), [stopCamera]);

  const applyValue = useCallback(
    (value: {
      status?: string;
      checkedInAt?: string;
      checkInOpensAt?: string;
      ticket?: ScanResultState['ticket'];
    }) => {
      const next: ScanResultState = {
        kind: toResultKind(value.status),
        checkedInAt: value.checkedInAt,
        opensAt: value.checkInOpensAt,
        ticket: value.ticket,
      };
      setResult(next);
      onResult?.(next);
      setBusy(false);
    },
    [onResult],
  );

  const reset = useCallback(() => {
    lastCodeRef.current = null;
    setResult(null);
    setBusy(false);
    setScanning(true);
  }, []);

  const handleScan = useCallback(
    async (text: string) => {
      if (!scanning || busy || !text) return;
      if (lastCodeRef.current === text) return;
      lastCodeRef.current = text;
      setBusy(true);
      setScanning(false);

      const response = await eventsApi.scanCheckIn(eventId, text, method);
      if (!response.success) {
        const next: ScanResultState = { kind: 'invalid' };
        setResult(next);
        onResult?.(next);
        setBusy(false);
        return;
      }
      applyValue(response.value);
    },
    [applyValue, busy, eventId, eventsApi, method, onResult, scanning],
  );

  const confirmGuest = useCallback(async () => {
    const code = lastCodeRef.current;
    if (!code || busy) return;
    setBusy(true);
    const response = await eventsApi.scanCheckIn(eventId, code, method, true);
    if (!response.success) {
      setResult({ kind: 'invalid' });
      setBusy(false);
      return;
    }
    applyValue(response.value);
  }, [applyValue, busy, eventId, eventsApi, method]);

  if (result) {
    return (
      <CheckInResultFlood
        kind={result.kind}
        audience={audience}
        firstName={result.ticket?.firstName}
        lastName={result.ticket?.lastName}
        email={result.ticket?.email}
        ticketNumber={result.ticket?.ticketNumber}
        ticketType={result.ticket?.ticketType}
        checkedInAt={result.checkedInAt}
        opensAt={result.opensAt}
        onScanNext={showScanNext ? reset : undefined}
        onClose={onClose}
        onConfirmGuest={() => {
          void confirmGuest();
        }}
        onDeclineGuest={reset}
        onShowMyCode={onShowMyCode}
        confirming={busy}
      />
    );
  }

  if (camera === 'denied') {
    return (
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          backgroundColor: CHECK_IN_COLORS.sheetBg,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '16px',
          px: '24px',
          pb: '24px',
          textAlign: 'center',
        }}
      >
        <NoPhotographyOutlinedIcon
          sx={{ fontSize: 48, color: CHECK_IN_COLORS.navy }}
        />
        <Box>
          <Typography
            sx={{
              fontFamily: 'Poppins, sans-serif',
              fontWeight: 600,
              fontSize: '18px',
              lineHeight: '26px',
              color: CHECK_IN_COLORS.text,
            }}
          >
            Camera access is off
          </Typography>
          <Typography
            sx={{
              mt: '8px',
              fontFamily: 'Poppins, sans-serif',
              fontWeight: 400,
              fontSize: '14px',
              lineHeight: '21px',
              color: CHECK_IN_COLORS.textSecondary,
            }}
          >
            Allow camera access in Settings, then come back to scan a code.
          </Typography>
        </Box>
        <Box
          onClick={openDeviceSettings}
          sx={{
            mt: '8px',
            width: '100%',
            maxWidth: '350px',
            height: '48px',
            borderRadius: '8px',
            backgroundColor: CHECK_IN_COLORS.gold,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
          }}
        >
          <Typography
            sx={{
              fontFamily: 'Poppins, sans-serif',
              fontWeight: 500,
              fontSize: '14px',
              color: CHECK_IN_COLORS.goldText,
            }}
          >
            Open Settings
          </Typography>
        </Box>
        {audience === 'attendee' && onShowMyCode ? (
          <Box
            onClick={onShowMyCode}
            sx={{
              width: '100%',
              maxWidth: '350px',
              height: '48px',
              borderRadius: '8px',
              border: `1px solid ${CHECK_IN_COLORS.navy}`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
            }}
          >
            <Typography
              sx={{
                fontFamily: 'Poppins, sans-serif',
                fontWeight: 500,
                fontSize: '14px',
                color: CHECK_IN_COLORS.navy,
              }}
            >
              Show My Code
            </Typography>
          </Box>
        ) : (
          <Box
            onClick={() => {
              void probeCamera();
            }}
            sx={{
              width: '100%',
              maxWidth: '350px',
              height: '48px',
              borderRadius: '8px',
              border: `1px solid ${CHECK_IN_COLORS.navy}`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
            }}
          >
            <Typography
              sx={{
                fontFamily: 'Poppins, sans-serif',
                fontWeight: 500,
                fontSize: '14px',
                color: CHECK_IN_COLORS.navy,
              }}
            >
              Try again
            </Typography>
          </Box>
        )}
      </Box>
    );
  }

  return (
    <Box
      ref={containerRef}
      sx={{
        flex: 1,
        minHeight: 0,
        backgroundColor: '#000',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {(busy || camera === 'checking') && (
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            zIndex: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: 'rgba(0,0,0,0.45)',
          }}
        >
          <CircularProgress sx={{ color: CHECK_IN_COLORS.gold }} />
        </Box>
      )}
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          '& video': {
            objectFit: 'cover',
            width: '100% !important',
            height: '100% !important',
          },
          '& section': { height: '100%' },
          '& div': { height: '100%' },
        }}
      >
        {scanning && camera === 'ready' && (
          <QrReader
            constraints={{ facingMode: 'environment' }}
            onResult={(res, err) => {
              if (err) return;
              const text = (res as any)?.getText
                ? (res as any).getText()
                : (res as any)?.text;
              if (text) void handleScan(text);
            }}
            containerStyle={{ width: '100%', height: '100%' }}
            videoStyle={{ width: '100%', height: '100%', objectFit: 'cover' }}
          />
        )}
      </Box>
      <Box
        sx={{
          position: 'absolute',
          left: '50%',
          top: '50%',
          transform: 'translate(-50%, -50%)',
          width: '220px',
          height: '220px',
          border: `2px solid ${CHECK_IN_COLORS.gold}`,
          borderRadius: '16px',
          pointerEvents: 'none',
          boxShadow: '0 0 0 9999px rgba(0,0,0,0.35)',
        }}
      />
      <Typography
        sx={{
          position: 'absolute',
          bottom: '32px',
          left: 0,
          right: 0,
          textAlign: 'center',
          color: CHECK_IN_COLORS.white,
          fontFamily: 'Poppins, sans-serif',
          fontSize: '14px',
          fontWeight: 500,
          px: '20px',
        }}
      >
        {method === 'staff_scan'
          ? 'Scan attendee ticket QR'
          : 'Scan the event code'}
      </Typography>
    </Box>
  );
};
